import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  onSnapshot, 
  doc, 
  updateDoc,
  addDoc,
  getDoc
} from 'firebase/firestore';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { db, auth } from '../firebase';
import { FaPlus, FaUserPlus } from 'react-icons/fa';
import CustomerCard from './customerCard';
import CustomerWindow from './customerWindow';
import CRMHeader from './crmHeader';
import './styles/crmMain.css';

const STATUS_TYPES = {
  lead: { 
    label: 'Lead', 
    color: '#ec4899',
    icon: '🎯'
  },
  contacted: { 
    label: 'Kontaktet', 
    color: '#eab308',
    icon: '📞'
  },
  qualified: { 
    label: 'Kvalificeret', 
    color: '#f97316',
    icon: '✓'
  },
  meeting: { 
    label: 'Møde', 
    color: '#2563eb',
    icon: '📅'
  },
  ongoing: { 
    label: 'Igangværende', 
    color: '#9333ea',
    icon: '⚡'
  },
  notInterested: { 
    label: 'Ikke interesseret', 
    color: '#6b7280',
    icon: '✕'
  },
  won: { 
    label: 'Vundet', 
    color: '#22c55e',
    icon: '🏆'
  }
};

const formatCurrency = (amount) => {
  if (!amount) return '0 kr';
  return new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: 'DKK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

function CRMMain({ onToggleMenu }) {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerWindow, setShowCustomerWindow] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [customFilters, setCustomFilters] = useState(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'customers'), (snapshot) => {
      const customersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(customersData);
    });

    return () => unsubscribe();
  }, []);

  const getFilteredCustomers = () => {
    let filtered = [...customers];

    // Først anvendes hovedfilter (alle/mine)
    if (activeFilter === 'mine') {
      filtered = filtered.filter(customer => customer.accountOwner === auth.currentUser.uid);
    }

    // Derefter anvendes custom filtre hvis de er aktive
    if (customFilters) {
      if (customFilters.owner) {
        filtered = filtered.filter(customer => customer.accountOwner === customFilters.owner);
      }

      if (customFilters.products && customFilters.products.length > 0) {
        filtered = filtered.filter(customer => 
          customer.products && customer.products.some(product => 
            customFilters.products.includes(product)
          )
        );
      }

      if (customFilters.urgency) {
        filtered = filtered.filter(customer => {
          // Ignorer vundne leads i urgency filter
          if (customer.status === 'won') return false;
          
          if (!customer.updates || customer.updates.length === 0) return false;
          
          const lastUpdate = customer.updates.reduce((latest, current) => 
            new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
          );
          
          const daysSinceLastUpdate = Math.floor(
            (new Date() - new Date(lastUpdate.timestamp)) / (1000 * 60 * 60 * 24)
          );

          if (customFilters.urgency === 'warning') {
            return daysSinceLastUpdate > 7;
          } else if (customFilters.urgency === 'critical') {
            return daysSinceLastUpdate > 14;
          }
          return false;
        });
      }
    }

    return filtered;
  };

  const handleFilterChange = (filterType, customFilters = null) => {
    setActiveFilter(filterType);
    setCustomFilters(customFilters);
  };

  const handleAddCustomer = async () => {
    try {
      // Opret nyt customer dokument med basis data
      const newCustomerRef = await addDoc(collection(db, 'customers'), {
        name: 'Ny kunde',
        status: 'lead',
        accountOwner: auth.currentUser.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        products: [],
        updates: []
      });

      // Hent det nye dokument for at få alle felter med
      const newCustomerDoc = await getDoc(newCustomerRef);
      const newCustomer = {
        id: newCustomerDoc.id,
        ...newCustomerDoc.data()
      };

      // Åbn customerWindow med den nye kunde
      setSelectedCustomer(newCustomer);
      setShowCustomerWindow(true);
    } catch (error) {
      console.error('Fejl ved oprettelse af ny kunde:', error);
    }
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setShowCustomerWindow(true);
  };

  const handleCloseCustomerWindow = () => {
    setShowCustomerWindow(false);
    setSelectedCustomer(null);
  };

  const filteredCustomers = getFilteredCustomers();

  const handleDragStart = () => {
    // Tilføj klasse til body under drag
    document.body.classList.add('dragging');
  };

  const handleDragEnd = async (result) => {
    document.body.classList.remove('dragging');
    
    const { destination, source, draggableId } = result;

    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }

    try {
      setCustomers(prevCustomers => {
        const updatedCustomers = [...prevCustomers];
        const movedCustomer = updatedCustomers.find(c => c.id === draggableId);
        if (movedCustomer) {
          movedCustomer.status = destination.droppableId;
        }
        return updatedCustomers;
      });

      await updateDoc(doc(db, 'customers', draggableId), {
        status: destination.droppableId
      });
    } catch (error) {
      console.error('Fejl ved opdatering af status:', error);
      // Hvis der er fejl, genindlæs data via onSnapshot
    }
  };

  return (
    <div className="crm-container">
      <CRMHeader 
        onToggleMenu={onToggleMenu}
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        onAddCustomer={handleAddCustomer}
      />
      <div className="crm-content">
        <DragDropContext 
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="board-container">
            {Object.entries(STATUS_TYPES).map(([status, { label, color, icon }]) => (
              <div key={status} className="board-column">
                <div className="column-header" style={{ borderBottom: `2px solid ${color}` }}>
                  <div className="column-header-top">
                    <h3>
                      <span>{icon}</span>
                      {label}
                    </h3>
                    <span className="customer-count">
                      {filteredCustomers.filter(customer => customer.status === status).length}
                    </span>
                  </div>
                  <div className="column-total">
                    {formatCurrency(
                      filteredCustomers
                        .filter(customer => customer.status === status)
                        .reduce((total, customer) => total + (customer.estimatedValue || 0), 0)
                    )}
                  </div>
                </div>
                <Droppable droppableId={status}>
                  {(provided, snapshot) => (
                    <div 
                      className={`column-content ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {filteredCustomers.filter(customer => customer.status === status).map((customer, index) => (
                        <Draggable 
                          key={customer.id} 
                          draggableId={customer.id} 
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className="draggable-wrapper"
                              style={{
                                ...provided.draggableProps.style,
                                transform: snapshot.isDragging 
                                  ? provided.draggableProps.style?.transform 
                                  : 'translate(0, 0)'
                              }}
                            >
                              <CustomerCard 
                                customer={customer}
                                statusColor={color}
                                onClick={() => {
                                  if (!snapshot.isDragging) {
                                    setSelectedCustomer(customer);
                                    setShowCustomerWindow(true);
                                  }
                                }}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>
      </div>
      {showCustomerWindow && selectedCustomer && (
        <CustomerWindow
          customer={selectedCustomer}
          isNew={selectedCustomer.isNew}
          onClose={handleCloseCustomerWindow}
          onUpdate={() => {
            // Opdateringen håndteres automatisk via onSnapshot
          }}
        />
      )}
    </div>
  );
}

export default CRMMain;
